import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI, coverLetterAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, ChevronLeft, Sparkles, Copy, Download, AlertCircle,
  CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const CoverLetterPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [cvs, setCVs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  
  const [formData, setFormData] = useState({
    cv_id: '',
    job_title: '',
    company_name: '',
    job_description: '',
    tone: 'professional'
  });

  const toneOptions = [
    { value: 'professional', label: 'Professional', description: 'Polished and competent' },
    { value: 'confident', label: 'Confident', description: 'Assertive and impactful' },
    { value: 'enthusiastic', label: 'Enthusiastic', description: 'Energetic and excited' },
    { value: 'formal', label: 'Formal', description: 'Traditional and conservative' }
  ];

  useEffect(() => {
    loadCVs();
  }, []);

  const loadCVs = async () => {
    try {
      const response = await cvAPI.list();
      setCVs(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, cv_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to load CVs');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!formData.cv_id) {
      toast.error('Please select a CV');
      return;
    }
    if (!formData.job_title.trim()) {
      toast.error('Please enter the job title');
      return;
    }
    if (!formData.company_name.trim()) {
      toast.error('Please enter the company name');
      return;
    }
    if (!formData.job_description.trim()) {
      toast.error('Please paste the job description');
      return;
    }

    setGenerating(true);
    try {
      const response = await coverLetterAPI.generate(formData);
      setCoverLetter(response.data.cover_letter);
      toast.success('Cover letter generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate cover letter');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(coverLetter);
    toast.success('Copied to clipboard!');
  };

  const handleDownload = async () => {
    try {
      // Call backend to generate PDF
      const response = await coverLetterAPI.downloadPdf({
        cover_letter: coverLetter,
        company_name: formData.company_name,
        job_title: formData.job_title
      });
      
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cover_Letter_${formData.company_name.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF Downloaded!');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Subscription Required</h1>
          <p className="text-slate-600 mb-6">Subscribe to access the Cover Letter Generator</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-600"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Dashboard
          </Button>
          <div className="h-6 w-px bg-slate-200"></div>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-500" />
            <h1 className="font-semibold text-slate-900">Cover Letter Generator</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Intro */}
          <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl p-6 md:p-8 text-white mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">AI-Powered Cover Letters</h2>
                <p className="text-violet-100">
                  Generate personalized, professional cover letters tailored to each job application.
                  Our AI uses your CV to highlight relevant experience - it never invents or exaggerates.
                </p>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-6">Job Details</h3>
              
              <div className="space-y-5">
                {/* CV Selection */}
                <div>
                  <Label>Select Your CV *</Label>
                  <Select 
                    value={formData.cv_id} 
                    onValueChange={(value) => setFormData({ ...formData, cv_id: value })}
                  >
                    <SelectTrigger className="mt-1.5 h-11" data-testid="cover-letter-cv-select">
                      <SelectValue placeholder="Choose a CV" />
                    </SelectTrigger>
                    <SelectContent>
                      {cvs.map((cv) => (
                        <SelectItem key={cv.id} value={cv.id}>
                          {cv.personal_details?.full_name || cv.title || 'Untitled CV'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {cvs.length === 0 && !loading && (
                    <p className="text-sm text-amber-600 mt-2">
                      No CVs found. <button onClick={() => navigate('/cv-builder')} className="underline">Create one first</button>
                    </p>
                  )}
                </div>

                {/* Job Title */}
                <div>
                  <Label>Job Title *</Label>
                  <Input
                    data-testid="cover-letter-job-title"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    placeholder="e.g., Sales Representative"
                    className="mt-1.5 h-11"
                  />
                </div>

                {/* Company Name */}
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    data-testid="cover-letter-company"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="e.g., Shoprite Holdings"
                    className="mt-1.5 h-11"
                  />
                </div>

                {/* Tone */}
                <div>
                  <Label>Writing Tone</Label>
                  <Select 
                    value={formData.tone} 
                    onValueChange={(value) => setFormData({ ...formData, tone: value })}
                  >
                    <SelectTrigger className="mt-1.5 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {toneOptions.map((tone) => (
                        <SelectItem key={tone.value} value={tone.value}>
                          <div>
                            <span className="font-medium">{tone.label}</span>
                            <span className="text-slate-500 ml-2">- {tone.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Job Description */}
                <div>
                  <Label>Job Description *</Label>
                  <Textarea
                    data-testid="cover-letter-job-desc"
                    value={formData.job_description}
                    onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
                    placeholder="Paste the full job description here..."
                    className="mt-1.5 min-h-[200px]"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    The more detail you provide, the better the cover letter will be tailored.
                  </p>
                </div>

                {/* Generate Button */}
                <Button
                  data-testid="generate-cover-letter-btn"
                  onClick={handleGenerate}
                  disabled={generating}
                  className="w-full h-12 bg-violet-600 hover:bg-violet-700 text-white"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Cover Letter
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Output */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-900">Your Cover Letter</h3>
                {coverLetter && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleCopy}>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDownload}>
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                )}
              </div>

              {coverLetter ? (
                <div className="bg-slate-50 rounded-xl p-6 min-h-[400px]">
                  <div className="prose prose-slate max-w-none">
                    <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">
                      {coverLetter}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-xl p-6 min-h-[400px] flex items-center justify-center">
                  <div className="text-center text-slate-400">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Your cover letter will appear here</p>
                    <p className="text-sm mt-1">Fill in the job details and click generate</p>
                  </div>
                </div>
              )}

              {coverLetter && (
                <div className="mt-4 p-4 bg-emerald-50 rounded-xl flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-emerald-800">
                    <p className="font-medium">Ready to send!</p>
                    <p className="mt-1">Review the letter and personalize if needed. Remember to proofread before sending.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tips */}
          <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Cover Letter Tips</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-medium text-slate-900 mb-1">Personalize It</p>
                <p className="text-sm text-slate-600">Add specific details about why you want to work for this company.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-medium text-slate-900 mb-1">Keep It Concise</p>
                <p className="text-sm text-slate-600">Aim for 3-4 paragraphs. Recruiters spend less than 30 seconds reading.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-medium text-slate-900 mb-1">Match Keywords</p>
                <p className="text-sm text-slate-600">Include relevant keywords from the job description.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CoverLetterPage;
